import Phaser from 'phaser';
import { loadSettings } from '@/config/settings';
import type { HitQuality } from '@/utils/timing';

export class AudioManager {
  private scene: Phaser.Scene;
  private sfxVolume: number;
  private musicVolume: number;
  private currentMusic: Phaser.Sound.BaseSound | null = null;

  // Sound keys
  private readonly SOUNDS = {
    hitPerfect: 'hit-perfect',
    hitGood: 'hit-good',
    hitOk: 'hit-ok',
    miss: 'miss',
    streak10: 'streak-10',
    streak25: 'streak-25',
    streak50: 'streak-50',
    streakBreak: 'streak-break',
  };

  // Morse sound key and markers
  private readonly MORSE_KEY = 'morse';
  private morseSound: Phaser.Sound.WebAudioSound | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    const settings = loadSettings();
    this.sfxVolume = settings.sfxVolume;
    this.musicVolume = settings.musicVolume;

    this.initMorseSound();
  }

  private initMorseSound(): void {
    if (!this.scene.cache.audio.exists(this.MORSE_KEY)) {
      return;
    }

    this.morseSound = this.scene.sound.add(this.MORSE_KEY) as Phaser.Sound.WebAudioSound;
    this.morseSound.addMarker({ name: 'dash', start: 0.025, duration: 0.19 });
    this.morseSound.addMarker({ name: 'dot', start: 0.265, duration: 0.07 });
  }

  /**
   * Play a sound effect if it exists
   */
  private playSfx(key: string, volumeMultiplier: number = 1): void {
    if (this.scene.sound.get(key) || this.scene.cache.audio.exists(key)) {
      try {
        this.scene.sound.play(key, {
          volume: this.sfxVolume * volumeMultiplier,
        });
      } catch (e) {
        // Sound not loaded, ignore
        console.warn(`Sound not available: ${key}`);
      }
    }
  }

  /**
   * Play dot sound
   */
  playDot(): void {
    if (this.morseSound) {
      this.morseSound.play('dot', { volume: this.sfxVolume });
    }
  }

  /**
   * Play dash sound
   */
  playDash(): void {
    if (this.morseSound) {
      this.morseSound.play('dash', { volume: this.sfxVolume });
    }
  }

  /**
   * Play hit sound based on quality
   */
  playHit(quality: HitQuality): void {
    switch (quality) {
      case 'perfect':
        this.playSfx(this.SOUNDS.hitPerfect);
        break;
      case 'good':
        this.playSfx(this.SOUNDS.hitGood);
        break;
      case 'ok':
        this.playSfx(this.SOUNDS.hitOk);
        break;
      case 'miss':
        this.playSfx(this.SOUNDS.miss);
        break;
    }
  }

  /**
   * Play miss sound
   */
  playMiss(): void {
    this.playSfx(this.SOUNDS.miss);
  }

  /**
   * Play streak milestone sound
   */
  playStreakMilestone(streak: number): void {
    if (streak === 10) {
      this.playSfx(this.SOUNDS.streak10);
    } else if (streak === 25) {
      this.playSfx(this.SOUNDS.streak25);
    } else if (streak >= 50) {
      this.playSfx(this.SOUNDS.streak50);
    }
  }

  /**
   * Play combo break sound
   */
  playStreakBreak(): void {
    this.playSfx(this.SOUNDS.streakBreak);
  }

  /**
   * Start background music
   */
  playMusic(key: string): void {
    if (this.currentMusic) {
      this.currentMusic.stop();
    }

    if (this.scene.cache.audio.exists(key)) {
      this.currentMusic = this.scene.sound.add(key, {
        volume: this.musicVolume,
        loop: true,
      });
      this.currentMusic.play();
    }
  }

  /**
   * Stop background music
   */
  stopMusic(): void {
    if (this.currentMusic) {
      this.currentMusic.stop();
      this.currentMusic = null;
    }
  }

  /**
   * Update volume settings
   */
  updateVolume(sfx?: number, music?: number): void {
    if (sfx !== undefined) {
      this.sfxVolume = sfx;
    }
    if (music !== undefined) {
      this.musicVolume = music;
      if (this.currentMusic && 'setVolume' in this.currentMusic) {
        (this.currentMusic as Phaser.Sound.WebAudioSound).setVolume(this.musicVolume);
      }
    }
  }

  /**
   * Mute all sounds
   */
  mute(): void {
    this.scene.sound.mute = true;
  }

  /**
   * Unmute all sounds
   */
  unmute(): void {
    this.scene.sound.mute = false;
  }

  /**
   * Toggle mute
   */
  toggleMute(): boolean {
    this.scene.sound.mute = !this.scene.sound.mute;
    return this.scene.sound.mute;
  }

  /**
   * Check if audio is available
   */
  isAudioAvailable(): boolean {
    return this.scene.sound.locked === false;
  }

  /**
   * Clean up
   */
  destroy(): void {
    this.stopMusic();
  }
}
